import { AnnouncementBase } from '.';
import { Log } from '../../../AuxillaryService/Log';
import { Signage } from '../../../Signage';
import { ReachAPI } from '../../../ReachApi';
import { Scheduler } from '../../../Utility/Scheduler';
import { $ } from '../../../../vendor/jquery';
import { moment } from '../../../../vendor/moment';
import { AnnouncementWidget } from '../AnnouncementWidget';
import { SocialMediaAnnouncementConfiguration, SocialMediaProviders } from '../types';
import { glom } from '../../../Signage/glom';
import { isNullish } from '../../../Utility/Nullish';
import type { PlayTimes } from '../PlayTimes';
import {
    facebookATemplate,
    facebookBTemplate,
    facebookCTemplate,
    instagramATemplate,
    instagramBTemplate,
    socialMediaTemplate,
    twitterATemplate,
    twitterBTemplate,
} from '../Templates';

function facebookTemplate(
    kind: 'a' | 'b' | 'c',
    context: Parameters<typeof facebookATemplate>[0] &
        Parameters<typeof facebookBTemplate>[0] &
        Parameters<typeof facebookCTemplate>[0]
): string {
    switch (kind) {
        case 'a':
            return facebookATemplate(context);
        case 'b':
            return facebookBTemplate(context);
        case 'c':
            return facebookCTemplate(context);
    }
}

function twitterTemplate(
    kind: 'a' | 'b',
    context: Parameters<typeof twitterATemplate>[0] & Parameters<typeof twitterBTemplate>[0]
): string {
    switch (kind) {
        case 'a':
            return twitterATemplate(context);
        case 'b':
            return twitterBTemplate(context);
    }
}

function instagramTemplate(
    kind: 'a' | 'b',
    context: Parameters<typeof instagramATemplate>[0] & Parameters<typeof instagramBTemplate>[0]
): string {
    switch (kind) {
        case 'a':
            return instagramATemplate(context);
        case 'b':
            return instagramBTemplate(context);
    }
}

/**
 * Announcement Social Media
 * @deprecated only used by legacy
 */
class AnnouncementSocialMedia extends AnnouncementBase {
    public declare configuration: SocialMediaAnnouncementConfiguration;

    public constructor(
        configuration: SocialMediaAnnouncementConfiguration,
        widget: AnnouncementWidget,
        playTimes: PlayTimes
    ) {
        super(configuration, widget, playTimes);

        this.#socialMediaType = configuration.template;
        this.#socialMediaTemplate = configuration.sm_template ?? 'a';
    }

    protected renderTemplate(): string {
        return socialMediaTemplate({
            containerElementId: this.containerElementId,
            announcement: {
                name: this.configuration.name,
                id: this.configuration.id,
            },
            widgetid: this.widget.id,
        });
    }

    /**
     * announcement social media type
     */
    readonly #socialMediaType: 'twitter' | 'facebook' | 'instagram';

    readonly #socialMediaTemplate: 'a' | 'b' | 'c';

    updateContent = async (): Promise<void> => {
        const apiSecurity = new ReachAPI.Security(glom.playerUuid, glom.securityToken);

        try {
            const results = await ReachAPI.deferredToPromise(
                ReachAPI.socialmedia.getPosts(
                    this.#socialMediaType,
                    5,
                    apiSecurity,
                    this.configuration.sm_id,
                    this.configuration.reach_sm_id,
                    'America/Denver'
                )
            );

            //TODO: document API return type, validate?
            const data = results.data;

            const socialMediaJqElement = $(this.widget.wrapperElement).find(
                `#socialmedia_${this.configuration.id}_widget_${this.widget.id}`
            );

            if (data.posts.length > 0) {
                const post = data.posts[0];

                if (!isNullish(post.img)) {
                    post.img = post.img.replace('&amp;', '&');
                }

                if (isNullish(post.imageheight) || '' === post.imageheight) {
                    post.imageheight = 0;
                }

                const bottom = this.configuration.height - 150;
                const textStart = 130 + post.imageheight;

                const userLang = navigator.language || 'en';

                //todo: replace with Luxon
                const publishDate = moment
                    .utc(post.date)
                    .locale(userLang)
                    .tz(Signage.localTimeZone);

                const sharedContext = {
                    announcement_id: this.configuration.id,
                    announcement: {
                        name: this.configuration.name,
                    },
                    width: this.configuration.width,
                    height: this.configuration.height,
                    max_img_width: this.configuration.width,
                    bottom: bottom,
                    pubDate: publishDate.format('LLLL'),
                };

                let postHtml: string | undefined;
                if (
                    this.#socialMediaType === SocialMediaProviders.FACEBOOK &&
                    (this.#socialMediaTemplate === 'a' ||
                        this.#socialMediaTemplate === 'b' ||
                        this.#socialMediaTemplate === 'c')
                ) {
                    postHtml = facebookTemplate(this.#socialMediaTemplate, {
                        ...sharedContext,
                        image:
                            post.object_id !== null
                                ? `https://graph.facebook.com/${post.object_id}/picture/?type=normal`
                                : post.picture,
                        message: post.message,
                        facebook_logo: '/images/facebook_logo.png',
                        logo: `https://graph.facebook.com/${this.configuration.sm_id}/picture/?type=normal`,
                    });
                } else if (
                    this.#socialMediaType === SocialMediaProviders.TWITTER &&
                    (this.#socialMediaTemplate === 'a' || this.#socialMediaTemplate === 'b')
                ) {
                    postHtml = twitterTemplate(this.#socialMediaTemplate, {
                        ...sharedContext,
                        image: '',
                        message: post.text,
                        textstart: textStart,
                        logo: String(post.profile_image_url ?? '').replace('_normal', '_400x400'),
                    });
                } else if (
                    this.#socialMediaType === SocialMediaProviders.INSTAGRAM &&
                    (this.#socialMediaTemplate === 'a' || this.#socialMediaTemplate === 'b')
                ) {
                    postHtml = instagramTemplate(this.#socialMediaTemplate, {
                        ...sharedContext,
                        image: post.image_url,
                        message: post.caption_text,
                    });
                }

                if (postHtml === undefined) {
                    //this is impossible
                    Log.Error(
                        `unsupported social media type "${this.#socialMediaType}(${
                            this.#socialMediaTemplate
                        })" for announcement (id=${this.configuration.id})`,
                        null,
                        __fileline
                    );
                }
                socialMediaJqElement.html(postHtml ?? '');

                //todo: refactor following jquery out of widget code. widget code ought not perform
                //      template-specific DOM manipulations.
                const messageInnerJqElement = $(this.widget.wrapperElement).find(
                    `#social-media-announcement-message-${this.configuration.id} div`
                );
                messageInnerJqElement.css('font-size', '100px'); //reset font size
                const messageWrapperJqElement = $(this.widget.wrapperElement).find(
                    `#social-media-announcement-message-${this.configuration.id}`
                );

                //todo: something better. This is a nasty little piece of code.
                let innerHeight = messageInnerJqElement.height();
                const wrapperHeight = messageWrapperJqElement.height();

                while (innerHeight && wrapperHeight && innerHeight > wrapperHeight) {
                    const currentFontSize = parseInt(messageInnerJqElement.css('font-size'));

                    //no sense going below 1. Also prevent infinite loop.
                    if (currentFontSize < 2) {
                        break;
                    }

                    messageInnerJqElement.css('font-size', `${currentFontSize - 1}px`);

                    innerHeight = messageInnerJqElement.height();
                }

                await socialMediaJqElement.waitForImages();
            }
        } catch (error) {
            Log.Warning(
                `Failed to get the Social Media (${
                    this.#socialMediaType
                }) Template for Announcements`,
                null,
                __fileline,
                { error: error }
            );

            throw error;
        }
    };

    async initialize(): Promise<void> {
        //todo: research which the scheduler instance is not being saved and
        //the scheduler is not stopped on widget stop
        Scheduler.New()
            .HasAction(this.updateContent)
            .RunEvery(5, 'minutes')
            .Starting(moment().add(300000, 'milliseconds'));

        await this.updateContent();
    }
}

export { AnnouncementSocialMedia };
